import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import type { CommitmentPaymentPlanUpdateRes } from '@sola/my-sdk/commitments/requests.ts';
import type {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.ts';

import { PaymentMethodIcon } from '../../atoms/payment-method-icon/payment-method-icon.tsx';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { createPaymentPlanSuccessAlert } from '../../utils/alerts.tsx';
import { getPaymentMethodLabel } from '../../utils/payment-methods.ts';
import { PaymentMethodSelectDialog } from '../payment-method-select-dialog/payment-method-select-dialog.tsx';

export const CommitmentDetailsPaymentPlan: React.FC<{
  commitment?: GraphQLCommitment | null;
  onCommitmentsUpdated?: (updatedCommitments: GraphQLCommitment[]) => void;
}> = ({ commitment, onCommitmentsUpdated }) => {
  const { t } = useTranslation('my');
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const { data, isLoading, isError } = useSupporterQuery(paymentMethodsQuery);

  const paymentMethodForCommitment = useMemo(() => {
    if (!data?.length || !commitment?.paymentMethodId) {
      return;
    }

    return data.find((pm) => pm?.id === commitment?.paymentMethodId);
  }, [data, commitment?.paymentMethodId]);

  if (!commitment) {
    return null;
  }

  const handlePaymentMethodSelection = async (
    result: CommitmentPaymentPlanUpdateRes,
  ) => {
    clearAlerts();
    addAlerts([createPaymentPlanSuccessAlert()]);
    onCommitmentsUpdated?.(result.commitmentUpdates?.updated ?? []);
  };

  if (isLoading) {
    return <Skeleton height={100} width={250} />;
  }

  if (isError) {
    return (
      <Alert
        variant="negative"
        title={t('paymentMethods.queryError.title', { count: 1 })}
        message={t('paymentMethods.queryError.message')}
        action={
          <a href={t('ctas.contactSupport.url')}>
            {t('ctas.contactSupport.text')}
          </a>
        }
      />
    );
  }

  return (
    <>
      {/* Intentionally opens the global Make A Payment flow rather than scoping payment to this commitment. */}
      {paymentMethodForCommitment ? (
        <>
          <div className="flex gap-comfy-md items-center">
            <PaymentMethodIcon
              paymentMethod={
                paymentMethodForCommitment as GraphQLSupporterPaymentMethod
              }
            />
            <span className="label-moderate label-lg label-neutral-potent">
              {getPaymentMethodLabel(paymentMethodForCommitment, t)}
            </span>
          </div>
          <PaymentMethodSelectDialog
            commitmentInternalId={commitment?.internalId}
            currentPaymentMethodId={paymentMethodForCommitment?.id}
            currentPaymentDay={commitment?.paymentPullDay}
            openTrigger={
              <LinkStyle noUnderline inline>
                <button type="button">
                  {t('commitments.details.paymentMethod.changeLabel')}
                </button>
              </LinkStyle>
            }
            onSave={handlePaymentMethodSelection}
            allowAddPaymentMethod
          />
        </>
      ) : (
        <Alert
          variant="warning"
          title={t('commitments.notRecurring.title')}
          message={t('commitments.notRecurring.message')}
          action={
            <PaymentMethodSelectDialog
              commitmentInternalId={commitment?.internalId}
              openTrigger={
                <LinkStyle noUnderline inline>
                  <button type="button">
                    {t('commitments.notRecurring.ctaText')}
                  </button>
                </LinkStyle>
              }
              onSave={handlePaymentMethodSelection}
              allowAddPaymentMethod
            />
          }
        />
      )}
    </>
  );
};
